import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, Calendar, Gauge, Fuel, Settings, ChevronLeft, CheckCircle, ArrowLeft, Send } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleDetail = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/vehicles/${id}`);
        setVehicle(response.data);
      } catch (error) {
        setVehicle(null);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contacts`, {
        ...formData,
        subject: `Demande pour: ${vehicle.title}`,
        vehicle_id: id
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      alert('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <h2 className="text-2xl font-bold text-white mb-4">Véhicule non trouvé</h2>
        <Link to="/vehicules" className="text-[#DC2626] font-semibold hover:underline">
          <ArrowLeft className="inline mr-2" size={20} /> Retour aux véhicules
        </Link>
      </div>
    );
  }

  const images = vehicle.images?.length > 0 ? vehicle.images : ['https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800'];
  const whatsappUrl = `https://api.whatsapp.com/send/?phone=33613851946&text=${encodeURIComponent(`Bonjour, je suis intéressé par le véhicule: ${vehicle.title} (${vehicle.price.toLocaleString('fr-FR')} €)`)}`;

  const specs = [
    { icon: Calendar, label: 'Année', value: vehicle.year },
    { icon: Gauge, label: 'Kilométrage', value: `${vehicle.mileage.toLocaleString('fr-FR')} km` },
    { icon: Fuel, label: 'Carburant', value: vehicle.fuel },
    { icon: Settings, label: 'Boîte', value: vehicle.transmission },
  ];

  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)', minHeight: '100vh' }} data-testid="vehicle-detail">
      {/* Breadcrumb */}
      <div className="border-b border-white/5" style={{ background: 'var(--bg-surface)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/vehicules" className="inline-flex items-center text-zinc-500 hover:text-white transition-colors text-sm" data-testid="back-to-vehicles">
            <ChevronLeft size={18} /> Retour aux véhicules
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="border border-white/5 overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
              <img src={images[selectedImage]} alt={vehicle.title} className="w-full h-80 lg:h-96 object-cover" data-testid="main-image" />
              {images.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto">
                  {images.map((img, index) => (
                    <button
                      key={`thumb-${img}-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`w-16 h-16 flex-shrink-0 overflow-hidden border-2 transition-all ${index === selectedImage ? 'border-[#DC2626]' : 'border-white/10 opacity-60 hover:opacity-100'}`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Vehicle Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="border border-white/5 p-6 mb-6" style={{ background: 'var(--bg-surface)' }}>
              <div className="mb-4">
                <span className={`inline-block text-xs font-semibold px-3 py-1 mb-3 ${vehicle.status === 'vendu' ? 'bg-zinc-700 text-zinc-300' : 'bg-green-600/20 text-green-400'}`}>
                  {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                </span>
                <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight" data-testid="vehicle-title">{vehicle.title}</h1>
                <p className="text-zinc-500 text-sm mt-1" style={{ fontFamily: 'var(--font-mono)' }}>{vehicle.brand} {vehicle.model}</p>
              </div>

              <div className="text-3xl lg:text-4xl font-extrabold text-[#DC2626] mb-6" data-testid="vehicle-price" style={{ fontFamily: 'var(--font-mono)' }}>
                {vehicle.price.toLocaleString('fr-FR')} €
              </div>

              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {specs.map((spec, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-white/5" style={{ background: 'var(--bg-elevated)' }}>
                    <spec.icon size={20} className="text-[#DC2626] flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{spec.label}</p>
                      <p className="text-white text-sm font-semibold">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="space-y-2.5">
                <a href="tel:+33613851946" className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="call-button">
                  <Phone size={18} /> Appeler : 06 13 85 19 46
                </a>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-semibold text-sm transition-all hover:bg-green-700" data-testid="whatsapp-button">
                  <MessageCircle size={18} /> Contacter sur WhatsApp
                </a>
                <button onClick={() => setShowContactForm(!showContactForm)} className="w-full inline-flex items-center justify-center gap-2 py-3.5 border border-white/20 text-white font-semibold text-sm transition-all hover:bg-white/5" data-testid="toggle-contact-form">
                  <Send size={16} /> Envoyer un message
                </button>
              </div>
            </div>

            {/* Contact Form */}
            {showContactForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="border border-white/5 p-6" style={{ background: 'var(--bg-surface)' }}>
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                    <h3 className="text-xl font-bold text-white mb-2">Message envoyé !</h3>
                    <p className="text-zinc-500 text-sm">Nous vous recontacterons très rapidement.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form space-y-3" data-testid="contact-form">
                    <h3 className="text-lg font-bold text-white mb-3">Demande d'information</h3>
                    <input type="text" placeholder="Votre nom" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required data-testid="contact-name" />
                    <input type="email" placeholder="Votre email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="contact-email" />
                    <input type="tel" placeholder="Votre téléphone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required data-testid="contact-phone" />
                    <textarea placeholder="Votre message" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} rows={4} required className="resize-none" data-testid="contact-message" />
                    <button type="submit" disabled={submitting} className="w-full py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C] disabled:opacity-50" data-testid="submit-contact">
                      {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    </button>
                  </form>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Description & Options */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <div className="border border-white/5 p-6" style={{ background: 'var(--bg-surface)' }}>
            <h2 className="text-lg font-bold text-white mb-4">Description</h2>
            <p className="text-zinc-400 text-sm whitespace-pre-line leading-relaxed" data-testid="vehicle-description">
              {vehicle.description || "Pas de description disponible."}
            </p>
          </div>

          {vehicle.options?.length > 0 && (
            <div className="border border-white/5 p-6" style={{ background: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-bold text-white mb-4">Équipements & Options</h2>
              <ul className="grid grid-cols-2 gap-2">
                {vehicle.options.map((option, index) => (
                  <li key={`opt-${option}-${index}`} className="flex items-center gap-2 text-sm text-zinc-400">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={14} />
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
